import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

export default function About() {
  return (
    <>
      <Seo
        title="About Riverstone Partners"
        description="Riverstone Partners connects businesses with specialized solutions—marketing, employee wellness, medical billing, shipping, and emerging opportunities—grounded in relationships."
        path="/about"
      />

      <section className="border-b border-charcoal/10 bg-sage/50 py-16 md:py-24">
        <div className="shell max-w-3xl">
          <p className="eyebrow text-forest">About Riverstone</p>
          <h1 className="mt-5 font-serif text-4xl font-light leading-[1.1] tracking-tight text-navy sm:text-5xl" data-testid="about-title">
            Built around your business. Grounded in relationships.
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="shell grid gap-14 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
          <Reveal>
            <div className="space-y-6 text-lg leading-relaxed text-charcoal/80">
              <p>
                Riverstone Partners helps businesses find practical solutions to real
                operating challenges—growing revenue, improving efficiency, supporting
                employees, and exploring new opportunities. We focus on reducing
                expenses and recovering value for our clients, and on building
                partnerships that last well past a first conversation.
              </p>
              <p>
                Our role is deliberately focused: <strong className="font-semibold text-charcoal">we advise, connect, and stay engaged.</strong>{" "}
                The specialized work itself—building websites, administering wellness
                programs, managing medical billing, analyzing shipping spend—is delivered
                by established providers we know and trust. We introduce the right
                specialists for your situation and remain a steady point of contact as
                you evaluate and implement.
              </p>
              <p>
                Today our practice centers on five solution areas: Marketing &amp;
                Website Design, Employee Health &amp; Wellness, Medical Claims &amp;
                Billing, Shipping &amp; Freight, and Emerging Opportunities—with a
                separate path for professionals who want to become Aspire Partners
                affiliates.
              </p>
            </div>

            <div className="mt-12 border-l-2 border-forest bg-sage/50 p-8 md:p-10">
              <p className="eyebrow text-forest">Our mission</p>
              <p className="mt-4 font-serif text-2xl leading-snug text-navy">
                “To reduce expenses and recover overpayments for our clients.”
              </p>
              <p className="mt-4 text-sm text-charcoal/60">
                — the mission Riverstone Partners has carried since its founding
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <img
              src={IMAGES.forestMountain}
              alt="An evergreen forest below a mountain ridge"
              width="1600"
              height="1100"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover shadow-soft"
            />
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-charcoal/50">
              The river cuts through; the stone endures. Both shape how we work.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-20 text-ivory" data-testid="about-values">
        <div className="shell grid gap-12 md:grid-cols-3">
          {[
            ["Rivers", "Clarity of direction. We help you find the path forward, not just any path."],
            ["Stone", "Endurance. Relationships and decisions built to hold up over time."],
            ["Forests", "Depth and patience. Good outcomes grow from well-tended roots."],
          ].map(([t, b], i) => (
            <Reveal key={t} delay={i * 0.08}>
              <p className="font-serif text-3xl font-light">{t}</p>
              <p className="mt-3 leading-relaxed text-ivory/70">{b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="shell text-center">
          <Reveal>
            <h2 className="font-serif text-3xl font-light tracking-tight text-navy sm:text-4xl">
              Let’s find your next step forward.
            </h2>
            <Link to="/contact" className="btn-primary mt-8" data-testid="about-cta">
              Start a Conversation <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
